import { useState, useCallback, useRef, useEffect } from 'react'

/**
 * Hook for a draggable split-pane divider (desktop only).
 * Returns the chat panel width in pixels and a ref for the drag handle.
 */
export default function useResizablePanel(defaultWidth = 420, minWidth = 280, maxRatio = 0.65) {
  const [chatWidth, setChatWidth] = useState(defaultWidth)
  const dragging = useRef(false)
  const containerRef = useRef(null)

  const onPointerDown = useCallback((e) => {
    e.preventDefault()
    dragging.current = true
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }, [])

  useEffect(() => {
    const onPointerMove = (e) => {
      if (!dragging.current || !containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const totalWidth = rect.width
      const x = (e.clientX ?? e.touches?.[0]?.clientX) - rect.left
      // Chat is on the right, so chat width = total - x
      let newChatWidth = totalWidth - x
      // Clamp
      newChatWidth = Math.max(minWidth, Math.min(totalWidth * maxRatio, newChatWidth))
      setChatWidth(newChatWidth)
    }

    const onPointerUp = () => {
      if (dragging.current) {
        dragging.current = false
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }

    window.addEventListener('mousemove', onPointerMove)
    window.addEventListener('mouseup', onPointerUp)
    window.addEventListener('touchmove', onPointerMove)
    window.addEventListener('touchend', onPointerUp)

    return () => {
      window.removeEventListener('mousemove', onPointerMove)
      window.removeEventListener('mouseup', onPointerUp)
      window.removeEventListener('touchmove', onPointerMove)
      window.removeEventListener('touchend', onPointerUp)
    }
  }, [minWidth, maxRatio])

  return { chatWidth, containerRef, onPointerDown }
}
